import { useEffect, useCallback, useRef } from 'react';

import { useFlag } from '../services/launchDarkly';

const DEFAULT_INACTIVITY_MINUTES = 20;

export const useInactivityReload = () => {
  const webInactivityReloadMins = useFlag('web_inactivity_reload_minutes', DEFAULT_INACTIVITY_MINUTES);
  const webInactivityReloadMS = webInactivityReloadMins * 60 * 1000;

  const lastActivityRef = useRef(Date.now());
  const reloadPendingRef = useRef(false);
  const reloadInitiatedRef = useRef(false);

  const handleUserActivity = useCallback(() => {
    if (reloadPendingRef.current && !reloadInitiatedRef.current) {
      reloadInitiatedRef.current = true;
      console.log('Reloading due to inactivity after user interaction...');
      window.location.reload();
      return;
    }

    if (!reloadPendingRef.current) {
      lastActivityRef.current = Date.now();
    }
  }, []);

  useEffect(() => {
    if (webInactivityReloadMS <= 0) {
      return; // No need to set up inactivity reload if it's disabled
    }

    // Update last activity time on mount
    lastActivityRef.current = Date.now();

    // Single interval to check time remaining and update state
    const interval = setInterval(() => {
      const timeSinceActivity = Date.now() - lastActivityRef.current;
      const remaining = Math.max(0, webInactivityReloadMS - timeSinceActivity);

      if (remaining === 0 && !reloadPendingRef.current) {
        console.log('Inactivity period expired, reload pending on next interaction...');
        reloadPendingRef.current = true;
      }
    }, 1000);

    // Events to track user activity
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click', 'keydown', 'touchmove'];

    // Add event listeners
    events.forEach((event) => {
      document.addEventListener(event, handleUserActivity);
    });

    // Cleanup
    return () => {
      clearInterval(interval);
      events.forEach((event) => {
        document.removeEventListener(event, handleUserActivity);
      });
    };
  }, [webInactivityReloadMS, handleUserActivity]);
};
